import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navbar } from '../Home/components/Navbar';
import { Footer } from '../Home/components/Footer';
import { BookingsGrid } from './components/BookingsGrid';
import { userService } from '../../utils/api/user.service';
import { SEO } from '../../components/SEO/SEO';
import { EditProfileModal } from './components/EditProfileModal';
import './Profile.css';

export const Profile = () => {
  const { user: authUser } = useAuth();
  const userId = authUser?.result?._id ;
  
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        const data = await userService.getProfileWithBookings(userId);
        
        if (data.result) {
          setProfileData(data);
        } else {
          setError(data.message || 'Failed to fetch profile data');
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err.message || 'An error occurred while fetching your profile');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfileData();
    }

    // Re-initialize Webflow IX2 if needed
    if (window.Webflow) {
      window.Webflow.destroy();
      window.Webflow.ready();
      window.Webflow.require('ix2').init();
    }
    window.scrollTo(0, 0);
  }, [userId]);

  // Merge API user data with auth user data (auth has photoURL from Firebase)
  const apiUser = profileData?.result?.user;
  const user = {
    ...(authUser?.result || {}),
    ...apiUser,
    // Ensure photoURL from authUser takes precedence if API doesn't have it
    photoURL: apiUser?.photoURL || apiUser?.profilePicture || apiUser?.avatar || authUser?.photoURL,
  };
  
  const bookings = profileData?.result?.bookings || [];
  const subscriptionBookings = bookings.filter(
    (booking) => booking?.eventId?.type === 'Routine' || booking?.subscriptionStartDate
  );
  const standardBookings = bookings.filter(
    (booking) => !(booking?.eventId?.type === 'Routine' || booking?.subscriptionStartDate)
  );
  const filteredBookings = activeTab === 'subscriptions' ? subscriptionBookings : standardBookings;

  const profilePic = user?.photoURL || user?.profilePicture || user?.avatar || user?.profile_image;
  const displayName = user?.full_name || 'User';
  const displayRole = user?.gender ? user.gender.toLowerCase() : 'member';

  const handleProfileUpdated = (updatedUser) => {
    setProfileData((prev) => {
      if (!prev?.result) return prev;
      return {
        ...prev,
        result: {
          ...prev.result,
          user: {
            ...prev.result.user,
            ...updatedUser
          }
        }
      };
    });
  };

  if (loading) {
    return (
      <div className="profile-page-wrapper">
        <Navbar dark={false} />
        <div className="loading-container">
          <div className="profile-loader"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="My Profile & Bookings | Hobi Hub - Manage Your Experiences"
        description="View your Hobi Hub profile, manage your bookings, and track your upcoming events. Access all your experiences in one place."
        keywords="my profile, my bookings, Hobi Hub account, event bookings, upcoming events, user dashboard"
        canonicalUrl="https://hobi.co.in/profile"
      />
      <div className="profile-page-wrapper hero-bg-listing-intel profile-v2">
        <Navbar dark={true} />
      
      <main className="profile-main-content">
        <div className="profile-shell">
          {error && <div className="error-message">{error}</div>}

          <section className="profile-hero">
            <div className="profile-hero-cover" />
            <div className="profile-hero-card">
              <div className="profile-hero-left">
                <div className="profile-avatar">
                  {profilePic ? (
                    <img src={profilePic} alt="Profile" />
                  ) : (
                    <div className="profile-avatar-fallback">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="profile-identity">
                  <div className="profile-name-row">
                    <h1>{displayName}</h1>
                    <span className="profile-role">{displayRole}</span>
                  </div>
                  <p className="profile-subtitle">Keep your experiences organized and your subscriptions in sync.</p>
                  <div className="profile-meta">
                    <span>{user?.email || 'email not added'}</span>
                    <span>{user?.phone || 'phone not added'}</span>
                  </div>
                </div>
              </div>
              <div className="profile-hero-actions">
                <button className="profile-ghost-btn" onClick={() => setIsEditModalOpen(true)}>Edit Profile</button>
                <button className="profile-primary-btn" onClick={() => setActiveTab('subscriptions')}>View Subscriptions</button>
              </div>
            </div>
          </section>

          <section className="profile-stats">
            <div className="stat-card">
              <span>Total Bookings</span>
              <strong>{standardBookings.length}</strong>
            </div>
            <div className="stat-card">
              <span>Active Subscriptions</span>
              <strong>{subscriptionBookings.length}</strong>
            </div>
            <div className="stat-card">
              <span>Upcoming Experiences</span>
              <strong>{bookings.length}</strong>
            </div>
          </section>

          <section className="profile-tabs">
            <div className="tabs">
              <button
                className={activeTab === 'bookings' ? 'tab active' : 'tab'}
                onClick={() => setActiveTab('bookings')}
              >
                Bookings
              </button>
              <button
                className={activeTab === 'subscriptions' ? 'tab active' : 'tab'}
                onClick={() => setActiveTab('subscriptions')}
              >
                Subscriptions
              </button>
            </div>
          </section>

          <section className="profile-content" style={{paddingBottom:100}}>
            <BookingsGrid
              bookings={filteredBookings}
              emptyMessage={activeTab === 'subscriptions'
                ? 'No active subscriptions yet. Renew or book a routine class.'
                : 'No bookings yet. Start exploring events!'}
            />
          </section>
        </div>
      </main>

      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={user}
        userId={userId}
        onSuccess={handleProfileUpdated}
      />

        <Footer />
      </div>
    </>
  );
};
